import { set } from "vue";
import {
  getAccountData,
  getValueData,
  fetchCoinPriceFull,
  getSupportedTokens,
} from "~/services/prices";
import { getUSDCBalance, getETHBalance } from "~/services/account";

export const state = () => ({
  walletAddress: null,
  transactions: null,
  portfolio: null,
  portfolioValue: null,
  portfolioChange24Hr: null,
  hasMoreTransactions: true,
  tokens: [],
  tokensMap: new Map(),
  usdcBalance: 0,
  ethBalance: 0,
});

export const getters = {
  walletAddress: (state) => state.walletAddress,
  transactions: (state) => state.transactions,
  portfolio: (state) => state.portfolio,
  portfolioValue: (state) => state.portfolioValue,
  portfolioChange24Hr: (state) => state.portfolioChange24Hr,
  hasMoreTransactions: (state) => state.hasMoreTransactions,
  tokens: (state) => state.tokens,
  tokensMap: (state) => state.tokensMap,
  usdcBalance: (state) => state.usdcBalance,
  ethBalance: (state) => state.ethBalance,
};

export const mutations = {
  setWalletAddress(state, address) {
    state.walletAddress = address;
  },
  setTransactions(state, transactions) {
    state.transactions = transactions;
  },
  setPortfolioData(state, { portfolio, value, change24Hr }) {
    state.portfolio = portfolio;
    state.portfolioValue = value;
    state.portfolioChange24Hr = change24Hr;
  },
  setHasMoreTransactions(state, hasMore) {
    state.hasMoreTransactions = hasMore;
  },
  addPriceData(state, prices) {
    state.transactions.forEach((tx) => {
      if (!tx.valueUSD) {
        if (tx.value_relation?.value && tx.value_relation.symbol in prices) {
          const tokenPrice = prices[tx.value_relation.symbol].USD.PRICE;
          return set(tx, "valueUSD", parseFloat(tx.value_relation.value) * tokenPrice);
        }

        if (tx.value.includes("ETH")) {
          const numericValue = parseFloat(tx.value.replace(" ETH", "").replace("-", ""));
          return set(tx, "valueUSD", numericValue * prices.ETH.USD.PRICE);
        }
      }
    });
  },
  setTokens(state, tokens) {
    state.tokens = tokens;

    const tokensMap = new Map();
    tokens.forEach((token) => {
      tokensMap.set(token.symbol, token);
    });
    state.tokensMap = tokensMap;
  },
  setUSDCBalance(state, balance) {
    state.usdcBalance = balance;
  },
  setETHBalance(state, balance) {
    state.ethBalance = balance;
  },
};

export const actions = {
  async loadWalletData({ commit, state, dispatch }, walletAddress) {
    if (walletAddress !== state.walletAddress) {
      commit("setWalletAddress", walletAddress);
      commit("setTransactions", null);
      commit("setPortfolioData", { portfolio: null, value: null, change24Hr: null });
      commit("setHasMoreTransactions", true);
    }
    commit("setWalletAddress", walletAddress);
    const { tokens, transactions } = await getAccountData(walletAddress, 30);
    commit("setTransactions", transactions);
    if (transactions.length < 30) {
      commit("setHasMoreTransactions", false);
    }
    const valueData = await getValueData(tokens, walletAddress);
    commit("setPortfolioData", valueData);
  },

  async addPriceData({ state, commit }) {
    if (!state.transactions?.length) return;
    const neededPrices = state.transactions
      .filter((tx) => !tx.valueUSD && tx.value_relation?.value)
      .map((tx) => tx.value_relation.symbol);
    neededPrices.push("ETH");

    const { RAW: prices } = await fetchCoinPriceFull(neededPrices);
    console.info({ prices });
    commit("addPriceData", prices);
  },

  async loadMoreTransactions({ commit, state }, txLimit = 30) {
    if (!state.transactions?.length) throw new Error("No transactions yet");

    const earliestBlock = state.transactions.at(-1).block;
    const { transactions } = await getAccountData(
      state.walletAddress,
      txLimit,
      earliestBlock
    );
    if (transactions.length) {
      commit("setTransactions", state.transactions.concat(transactions));
    }

    if (!transactions.length || transactions.length < txLimit) {
      commit("setHasMoreTransactions", false);
    }
  },

  async loadSupportedTokens({ commit, state }) {
    if (!state.tokens?.length) commit("setTokens", await getSupportedTokens());
  },

  async updateUSDCBalance({ commit, state }) {
    const balance = await getUSDCBalance();
    commit("setUSDCBalance", balance);
  },

  async updateETHBalance({ commit, state }) {
    const balance = await getETHBalance();
    commit("setETHBalance", balance);
  },

  updateBalances({ dispatch }) {
    dispatch("updateUSDCBalance");
    dispatch("updateETHBalance");
  },
};
